/**
 * Project board motion helpers (Pipedrive-style celebrations & a11y).
 */

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Lightweight canvas burst — no extra dependency required. */
function fireFallbackConfetti(originY: number): void {
  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999";
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  ctx.scale(dpr, dpr);

  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#a855f7", "#06b6d4"];
  const cx = window.innerWidth / 2;
  const cy = window.innerHeight * originY;
  const particles = Array.from({ length: 120 }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 4 + Math.random() * 8;
    return {
      x: cx,
      y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 2,
      g: 0.12 + Math.random() * 0.08,
      size: 3 + Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 1,
      decay: 0.012 + Math.random() * 0.01,
    };
  });

  let frame = 0;
  const tick = () => {
    frame += 1;
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    let alive = false;
    for (const p of particles) {
      if (p.life <= 0) continue;
      alive = true;
      p.vy += p.g;
      p.x += p.vx;
      p.y += p.vy;
      p.life -= p.decay;
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x, p.y, p.size, p.size * 0.7);
    }
    ctx.globalAlpha = 1;
    if (alive && frame < 120) {
      requestAnimationFrame(tick);
    } else {
      canvas.remove();
    }
  };
  requestAnimationFrame(tick);
}

/**
 * Fire a one-shot won celebration (confetti). Always on; no-ops only when
 * reduced-motion is preferred. Prefers `canvas-confetti`; falls back to a
 * tiny built-in canvas burst if the import fails.
 */
export async function celebrateWonDeal(opts?: {
  originY?: number;
}): Promise<void> {
  if (prefersReducedMotion()) return;
  const originY = opts?.originY ?? 0.3;
  try {
    const confetti = (await import("canvas-confetti")).default;
    confetti({
      particleCount: 120,
      spread: 70,
      origin: { y: originY },
      disableForReducedMotion: true,
    });
  } catch {
    fireFallbackConfetti(originY);
  }
}

export function formatWonToast(valueEur?: number | null): string {
  if (valueEur === null || valueEur === undefined || Number.isNaN(Number(valueEur))) {
    return "Marked as Won";
  }
  const formatted = new Intl.NumberFormat("lt-LT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(valueEur));
  return `Marked as Won — ${formatted}`;
}

/** Idle/rotting: true when stage has rottingDays and last activity is older. */
export function isCardRotting(
  lastActivityAt: number | string | null | undefined,
  rottingDays: number | null | undefined
): boolean {
  if (rottingDays === null || rottingDays === undefined || rottingDays <= 0) {
    return false;
  }
  if (lastActivityAt === null || lastActivityAt === undefined) return false;
  const ts = Number(lastActivityAt);
  if (!Number.isFinite(ts) || ts <= 0) return false;
  const idleMs = Date.now() - ts;
  return idleMs > rottingDays * 24 * 60 * 60 * 1000;
}

export function idleDaysLabel(
  lastActivityAt: number | string | null | undefined
): number | null {
  if (lastActivityAt === null || lastActivityAt === undefined) return null;
  const ts = Number(lastActivityAt);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  return Math.max(0, Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000)));
}
